import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';

// The height to assign so the field shows all of its content.
//
// `scrollHeight` spans the content and the padding but stops at the border,
// whereas a height assigned to a `box-sizing: border-box` element (App.css sets
// that on everything) has to span the border too. `offsetHeight - clientHeight`
// is exactly the border, so adding it back keeps the last line from being
// clipped and stops a scrollbar appearing on a single-line message.
export function contentHeight({ scrollHeight, offsetHeight, clientHeight }) {
  return scrollHeight + (offsetHeight - clientHeight);
}

// Enter raised by an IME mid-composition, which must accept the candidate word
// rather than send. Most engines say so via `isComposing`; Android and older
// Safari instead report the legacy keyCode 229.
function isComposing(e) {
  return e.nativeEvent.isComposing || e.keyCode === 229;
}

// The message entry field shared by the chat, discussion and persona-builder
// screens. It is a textarea rather than an <input> so a message can carry line
// breaks: Shift+Enter inserts one, Enter sends. Because a textarea does not
// submit its form on Enter, the send is raised explicitly.
function ChatInput({ value, onChange, onSubmit, placeholder, disabled }) {
  const fieldRef = useRef(null);

  // Grow the box with its content; CSS caps it and takes over with a scrollbar.
  const resize = useCallback(() => {
    const field = fieldRef.current;
    if (!field) {
      return;
    }
    field.style.height = 'auto';
    field.style.height = `${contentHeight(field)}px`;
  }, []);

  useLayoutEffect(resize, [value, resize]);

  // Narrowing the field re-wraps the text onto more lines, which changes the
  // height even though `value` did not: the chat column shrinks whenever the
  // discussion panel opens, and again on every window resize.
  useEffect(() => {
    const field = fieldRef.current;
    if (!field || typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    let lastWidth = field.clientWidth;
    const observer = new ResizeObserver(() => {
      // `resize` writes the height, which notifies this observer straight back:
      // only a width change can re-wrap the text, so ignore anything else.
      if (field.clientWidth === lastWidth) {
        return;
      }
      lastWidth = field.clientWidth;
      resize();
    });
    observer.observe(field);
    return () => observer.disconnect();
  }, [resize]);

  const handleKeyDown = (e) => {
    if (e.key !== 'Enter' || e.shiftKey || isComposing(e)) {
      return;
    }
    e.preventDefault();
    onSubmit(e);
  };

  return (
    <textarea
      ref={fieldRef}
      rows={1}
      className="chat-input"
      value={value}
      onChange={onChange}
      onKeyDown={handleKeyDown}
      placeholder={placeholder}
      disabled={disabled}
    />
  );
}

export default ChatInput;
